/**
 * Shared host for popups that must escape a dialog's scroll container.
 *
 * A Modal/Drawer body is `overflow-y-auto` (only the body scrolls), and CSS turns the
 * other axis into `auto` too — so anything rendered INSIDE the body is clipped at its
 * edges. Radix popups (Select, Popover) portal themselves out and are fine; the
 * react-datepicker calendar renders inline, so a picker near a dialog's right edge had
 * its last column sliced off. Pointing react-datepicker at this portal id lifts the
 * calendar to <body> — out of the clipping box — while floating-ui keeps positioning it
 * against the field.
 *
 * The trade-off is that the calendar is then, in DOM terms, OUTSIDE the Radix dialog, so
 * Radix reads a click on it as an outside-interaction and would dismiss the dialog. The
 * dialogs therefore run every outside-interaction through {@link isInsidePopoverPortal}
 * and ignore the ones that came from this layer.
 */
export const POPOVER_PORTAL_ID = 'gb-popover-portal';

/**
 * floating-ui middleware for react-datepicker's `popperModifiers`: keep the calendar on screen.
 *
 * react-datepicker only FLIPS its calendar, it never shifts it. On a phone a field sitting right of
 * centre put the calendar's far edge past the screen — with the time list it is 327px wide — and the
 * whole page scrolled sideways. The position is clamped in viewport terms (8px gutter), so it holds
 * whatever element the calendar is positioned against, including the portal root above.
 *
 * Vertically too: in a phone dialog the date-and-time calendar (479px) flipped ABOVE its field and opened
 * 174px past the top of the screen, where a scroll-locked page can never bring its month arrows back.
 * It is pulled down into view whenever it fits the viewport; one taller than the viewport is left
 * where floating-ui put it, so scrolling can still reach its far end.
 */
export const keepPopperOnScreen = {
  name: 'keepPopperOnScreen',
  fn: ({ x, y, rects, elements }: { x: number; y: number; rects: { reference: { x: number; y: number }; floating: { width: number; height: number } }; elements: { reference: unknown } }) => {
    const gutter = 8;
    const viewport = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;
    const reference = elements.reference as { getBoundingClientRect?: () => DOMRect };
    const box = reference.getBoundingClientRect?.();
    const referenceLeft = box?.left ?? rects.reference.x;
    const left = referenceLeft + (x - rects.reference.x);
    const clamped = Math.max(gutter, Math.min(left, viewport - rects.floating.width - gutter));
    const referenceTop = box?.top ?? rects.reference.y;
    const top = referenceTop + (y - rects.reference.y);
    const lowestTop = viewportHeight - rects.floating.height - gutter;
    const clampedTop = lowestTop >= gutter ? Math.max(gutter, Math.min(top, lowestTop)) : top;
    return { x: x + (clamped - left), y: y + (clampedTop - top) };
  },
};

/** True when a Radix dismiss event originated inside the shared popup portal. */
export function isInsidePopoverPortal(event: { detail?: { originalEvent?: Event } }): boolean {
  const target = event.detail?.originalEvent?.target;
  return target instanceof Element && !!target.closest(`#${POPOVER_PORTAL_ID}`);
}
